import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useReducer,
} from 'react'

import { Box, IconButton } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import ReactGA from 'react-ga4'

import { routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { DayContext } from 'src/lib/dayContext'
import { ungatedForAssistant } from 'src/lib/gates'
import { logger } from 'src/lib/logger'
import {
  NativeObjectTypes,
  ObjectTypeMetadata,
  type NativeObjectType,
} from 'src/lib/objects'
import { addToRecentObjects } from 'src/lib/recentObjects'

import ObjectProvider from '../Objects/ObjectProvider'
import SidebarContent from '../Objects/ObjectSidebar/SidebarContent'
import SidebarProvider from '../Objects/ObjectSidebar/SidebarProvider'
import { sidebarSx } from '../Objects/ObjectSidebar/style'
import { PipelineProvider } from '../PipelineV2/PipelineDetail.tsx/PipelineContext'
import Row from '../Row/Row'

import OpportunityEditor from './OpportunityEditor/OpportunityEditor'
import SidebarHistory from './SidebarHistory/SidebarHistory'
import SidebarLayoutAction from './SidebarLayoutAction/SidebarLayoutAction'
import SidebarLayoutClip from './SidebarLayoutClip/SidebarLayoutClip'
import SidebarLayoutContact from './SidebarLayoutContact/SidebarLayoutContact'
import { SidebarLayoutDraft } from './SidebarLayoutDraft/SidebarLayoutDraft'
import SidebarLayoutEvent from './SidebarLayoutEvent/SidebarLayoutEvent'
import SidebarLayoutGmailThread from './SidebarLayoutGmailThread/SidebarLayoutGmailThread'
import SidebarLayoutMeetingRecording from './SidebarLayoutMeetingRecording/SidebarLayoutMeetingRecording'
import SidebarLayoutOpportunity from './SidebarLayoutOpportunity/SidebarLayoutOpportunity'
import SidebarLayoutOrganization from './SidebarLayoutOrganization/SidebarLayoutOrganization'
import SidebarLayoutPage from './SidebarLayoutPage/SidebarLayoutPage'
import SidebarLayoutPipeline from './SidebarLayoutPipeline/SidebarLayoutPipeline'
import SidebarLayoutSlackChannel from './SidebarLayoutSlackChannel/SidebarLayoutSlackChannel'
import SidebarLayoutUserContext from './SidebarLayoutUserContext/SidebarLayoutUserContext'
import SidebarObjectCreate from './SidebarObjectCreate/SidebarObjectCreate'
import SidebarPropertyDetail from './SidebarPropertyDetail.tsx/SidebarPropertyDetail'

/*
# Sidebar Navigation Test Requirements
import { SidebarMode } from '../Objects/ObjectSidebar/SidebarContext'

## Test Case 1: Circular Navigation
User opens Object A sidebar → clicks Object B → clicks Object A
- Back arrow remains visible and functional
- Forward arrow not present
- Object chips remain clickable
- Back navigation shows correct history

## Test Case 2: Reciprocal Navigation
User opens Object B sidebar → clicks Object A → clicks Object B
- Back arrow remains visible and functional
- Forward arrow not present
- All chips remain clickable
- Navigation history preserved

## Test Case 3: Single Back Navigation
User opens Object A sidebar → clicks Object B chip → clicks back arrow
- Object B chip remains clickable on Object A sidebar
- All navigation arrows function correctly
- Navigation state preserved

## Test Case 4: Multiple Object Chain
User opens Object A sidebar → clicks Object B chip → clicks Object C chip → clicks back button
- Object C chip remains clickable on Object B sidebar
- Back/forward arrows appear appropriately
- Full navigation chain preserved

## Test Case 5: Multiple Back Navigation
User opens Object A sidebar → clicks Object B chip → clicks Object C chip → clicks back twice
- Object C chip remains clickable from Object A sidebar
- Navigation history maintained
- All object chips remain functional

## Test Case 6: Linear Chain Navigation
User opens Object A sidebar → clicks Object B chip → clicks Object C chip → clicks back → clicks back
- Each back click returns to previous state
- Forward/back arrows appear appropriately
- All chips remain clickable throughout

## Test Case 7: Bidirectional Arrow Navigation
User opens Object A sidebar → clicks Object B chip → clicks Object C chip
- Forward arrow navigates to next state
- Back arrow navigates to previous state
- All navigation states preserved
- All chips remain clickable

## Test Case 8: Full Circle Navigation
User opens Object A sidebar → clicks Object B chip → clicks Object C chip → clicks Object A chip → clicks Object B chip
- Complete navigation chain preserved
- All chips remain clickable
- Back/forward arrows appear appropriately
- Navigation history maintained in both directions

## Requirements for All Cases:
1. Navigation arrows must appear/disappear appropriately based on position in history
2. All object chips must remain clickable after navigation
3. Navigation history must be preserved in both directions
4. Back/forward navigation must restore correct sidebar state
5. Clicking any chip must preserve the navigation chain
6. Multiple navigation paths to the same object must work correctly

*/

const Ga4PageTitle = {
  [NativeObjectTypes.Contact]: 'Person',
  [NativeObjectTypes.Organization]: 'Organization',
  [NativeObjectTypes.Opportunity]: 'Opportunity',
  [NativeObjectTypes.Action]: 'Action',
  [NativeObjectTypes.Event]: 'Event',
  [NativeObjectTypes.GmailThread]: 'Gmail Thread',
  [NativeObjectTypes.MeetingRecording]: 'Meeting Recording',
  [NativeObjectTypes.Page]: 'Page',
  [NativeObjectTypes.Pipeline]: 'Pipeline',
  [NativeObjectTypes.MeetingRecordingClip]: 'Meeting Recording Clip',
}

type NavigationState = {
  history: any[]
  currentIndex: number
}

type NavigationAction =
  | { type: 'ADD_OBJECT'; payload: any }
  | { type: 'NAVIGATE_BACK' }
  | { type: 'NAVIGATE_FORWARD' }
  | { type: 'RESET' }
  | { type: 'OBJECT_UPDATED'; payload: any }

function navigationReducer(
  state: NavigationState,
  action: NavigationAction
): NavigationState {
  let newHistory: any[]

  switch (action.type) {
    case 'ADD_OBJECT':
      // Skip if we're already at this object
      if (
        getObjectId(state.history[state.currentIndex]) ===
        getObjectId(action.payload)
      ) {
        return state
      }
      // Truncate history at current index and add new object
      newHistory = [
        ...state.history.slice(0, state.currentIndex + 1),
        action.payload,
      ]
      return {
        history: newHistory,
        currentIndex: state.currentIndex + 1,
      }
    case 'NAVIGATE_BACK':
      if (state.currentIndex > 0) {
        return { ...state, currentIndex: state.currentIndex - 1 }
      }
      return state
    case 'NAVIGATE_FORWARD':
      if (state.currentIndex < state.history.length - 1) {
        return { ...state, currentIndex: state.currentIndex + 1 }
      }
      return state
    case 'OBJECT_UPDATED': {
      const newHistory = [
        ...state.history.slice(0, state.currentIndex),
        action.payload,
        ...state.history.slice(state.currentIndex + 1, state.history.length),
      ]
      return { ...state, history: newHistory }
    }
    case 'RESET':
      return { history: [], currentIndex: -1 }
    default:
      return state
  }
}

const getObjectId = (object: any) => {
  if (object?.mode) {
    return `${object.objectId}-${object.mode}`
  }
  return `${object?.objectId}-view`
}

const USE_NEW_NAVIGATION = true // Feature flag for new navigation system

const ASSISTANT_SIDEBAR_OBJECT_TYPES = [NativeObjectTypes.Organization]

const Sidebar = ({
  isOpen = false,
  crmObject,
  setCrmObject,
  onClose = () => {},
  workspaceId,
}: {
  isOpen?: boolean
  crmObject: any
  setCrmObject: (crmObject: any) => void
  onClose?: () => void
  workspaceId?: string
}) => {
  const { currentUser } = useAuth()

  // Old navigation state
  const [objectHistory, setObjectHistory] = useState<any[]>([])
  const currentIndexRef = useRef(-1)
  const [_navigationCounter, setNavigationCounter] = useState(0) // Prefix with _ to indicate intentionally unused

  // New navigation state
  const [navigationState, dispatch] = useReducer(navigationReducer, {
    history: [],
    currentIndex: -1,
  })

  const { internalDomains, orgsByDomain } = useContext(DayContext)
  const [open, setOpen] = useState(isOpen)

  const sidebarRef = useRef<HTMLDivElement>(null)
  const beforeCloseRef = useRef<(() => Promise<boolean>) | null>(null)

  // Handle close with unsaved changes check
  const handleClose = useCallback(async () => {
    // Check if there's a form with unsaved changes
    if (beforeCloseRef.current) {
      const canClose = await beforeCloseRef.current()
      if (!canClose) {
        return // Don't close if form has unsaved changes and user cancelled
      }
    }
    setOpen(false)
  }, [])

  const trackSidebarNavigation = useCallback(
    ({
      objectType,
      objectId,
    }: {
      objectType: NativeObjectType
      objectId: string
    }) => {
      const objectTypeSlug =
        ObjectTypeMetadata?.[objectType]?.slug || objectType
      if (!objectTypeSlug) {
        return
      }

      const path = routes.objectDetail({
        objectTypeSlug,
        objectId,
        workspaceId,
      })

      if (!path) {
        return
      }

      const payload = {
        hitType: 'pageview' as const,
        page: path,
        title: Ga4PageTitle[objectType],
        contentGroup1: 'Sidebar',
        userId: currentUser?.id,
      }

      if (path) {
        try {
          ReactGA.send(payload)
        } catch (e) {
          logger.warn('GA tracking error')
        }
      }
    },
    [currentUser?.id, workspaceId]
  )

  // This effect handles new object selection
  useEffect(() => {
    if (!open || !crmObject?.objectId) {
      return
    }

    const objectId = getObjectId(crmObject)

    trackSidebarNavigation({
      objectType: crmObject?.objectType,
      objectId,
    })

    const objectWithWorkspace = {
      ...crmObject,
      workspaceId: crmObject.workspaceId || workspaceId,
    }

    if (USE_NEW_NAVIGATION) {
      dispatch({ type: 'ADD_OBJECT', payload: objectWithWorkspace })
      setCrmObject(null)
      return
    }

    // Old navigation logic
    if (objectHistory.length === 0) {
      setObjectHistory([objectWithWorkspace])
      currentIndexRef.current = 0
      setNavigationCounter((prev) => prev + 1)
      setCrmObject(null)
      return
    }

    if (getObjectId(objectHistory[currentIndexRef.current]) === objectId) {
      return
    }

    setObjectHistory((prev) => {
      const historyToKeep = prev.slice(0, currentIndexRef.current + 1)
      return [...historyToKeep, objectWithWorkspace]
    })
    currentIndexRef.current = currentIndexRef.current + 1
    setNavigationCounter((prev) => prev + 1)
    setCrmObject(null)
  }, [
    open,
    crmObject,
    workspaceId,
    trackSidebarNavigation,
    setCrmObject,
    objectHistory,
  ])

  const handleBack = useCallback(() => {
    if (USE_NEW_NAVIGATION) {
      dispatch({ type: 'NAVIGATE_BACK' })
      return
    }

    if (currentIndexRef.current > 0) {
      currentIndexRef.current--
      setNavigationCounter((prev) => prev + 1)
    }
  }, [])

  const handleForward = useCallback(() => {
    if (USE_NEW_NAVIGATION) {
      dispatch({ type: 'NAVIGATE_FORWARD' })
      return
    }

    if (currentIndexRef.current < objectHistory.length - 1) {
      currentIndexRef.current++
      setNavigationCounter((prev) => prev + 1)
    }
  }, [objectHistory.length])

  const currentObject = useMemo(() => {
    if (USE_NEW_NAVIGATION) {
      return navigationState.history[navigationState.currentIndex]
    }
    return objectHistory[currentIndexRef.current]
  }, [navigationState, objectHistory])

  const hasBack = USE_NEW_NAVIGATION
    ? navigationState.currentIndex > 0
    : currentIndexRef.current > 0

  const hasForward = USE_NEW_NAVIGATION
    ? navigationState.currentIndex < navigationState.history.length - 1
    : currentIndexRef.current < objectHistory.length - 1

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    if (!open) {
      if (USE_NEW_NAVIGATION) {
        dispatch({ type: 'RESET' })
      } else {
        setObjectHistory([])
        currentIndexRef.current = -1
      }
      onClose()
    }
  }, [open, onClose])

  useEffect(() => {
    if (
      crmObject?.objectId &&
      !internalDomains.includes(crmObject?.properties?.domain) &&
      !internalDomains.includes(crmObject?.properties?.domain?.value) &&
      !internalDomains.includes(crmObject?.objectId)
    ) {
      setOpen(true)
    }
  }, [crmObject, internalDomains])

  // Add object to recent list when sidebar opens with new object
  useEffect(() => {
    if (crmObject?.objectId && open) {
      addToRecentObjects(crmObject, workspaceId, currentUser?.id)
    }
  }, [crmObject, open, workspaceId, currentUser?.id])

  const history = {
    hasBack,
    hasForward,
    handleBack,
    handleForward,
    objectHistory: USE_NEW_NAVIGATION ? navigationState.history : objectHistory,
    currentIndex: USE_NEW_NAVIGATION
      ? navigationState.currentIndex
      : currentIndexRef.current,
  }

  if (currentObject && !currentObject?.workspaceId) {
    currentObject.workspaceId = workspaceId
  }

  useEffect(() => {
    const handleClickOutside = async (event: MouseEvent) => {
      if (!open || !sidebarRef.current) return

      const dialogContainer = document.querySelector('.MuiDialog-container')
      if (dialogContainer) {
        return // No-op when dialog is present
      }

      const sidebarRect = sidebarRef.current.getBoundingClientRect()
      const clickX = event.clientX

      // If click is to the left of the sidebar
      if (clickX < sidebarRect.left) {
        if (['create', 'edit'].includes(currentObject?.mode)) {
          // Use the unified close handler that checks for unsaved changes
          await handleClose()
        } else {
          setOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, currentObject?.mode])

  return (
    open && (
      <Box
        ref={sidebarRef}
        sx={sidebarSx}
      >
        <Box className="sidebar-outer">
          <Row className="sidebar-navigation">
            <SidebarHistory history={history} />

            <Row gap={1}>
              <IconButton
                sx={{
                  height: '32px',
                  width: '32px',
                  opacity: 1,
                }}
                onClick={handleClose}
              >
                <IconX
                  size={24}
                  style={{
                    flexShrink: 0,
                  }}
                />
              </IconButton>
            </Row>
          </Row>
          {currentObject &&
          ASSISTANT_SIDEBAR_OBJECT_TYPES.includes(currentObject.objectType) &&
          ungatedForAssistant(currentUser) ? (
            <ObjectProvider
              workspaceId={workspaceId}
              objectId={currentObject.objectId}
              objectType={currentObject.objectType}
            >
              <SidebarProvider
                workspaceId={workspaceId}
                onClose={() => setOpen(false)}
              >
                <SidebarContent />
              </SidebarProvider>
            </ObjectProvider>
          ) : (
            <Box className="sidebar-content">
              {['create', 'edit'].includes(currentObject?.mode) ? (
                <>
                  {currentObject.objectType ===
                  NativeObjectTypes.Opportunity ? (
                    <OpportunityEditor
                      mode={currentObject.mode}
                      workspaceId={currentObject.workspaceId}
                      initialData={{
                        ...currentObject.properties,
                        selectedOrganization: currentObject.properties?.domain
                          ? orgsByDomain[currentObject.properties.domain]
                          : null,
                        title:
                          currentObject.properties?.title ||
                          (currentObject.properties?.domain
                            ? orgsByDomain?.[currentObject.properties.domain]
                                ?.name
                            : null),
                        selectedOwner: currentUser,
                      }}
                      onComplete={() => {
                        setOpen(false)
                      }}
                      onUpdate={(newObject) =>
                        dispatch({ type: 'OBJECT_UPDATED', payload: newObject })
                      }
                    />
                  ) : (
                    <SidebarObjectCreate
                      objectType={currentObject.objectType}
                      mode={currentObject.mode}
                      workspaceId={currentObject.workspaceId}
                      initialData={{
                        ...currentObject.properties,
                        selectedOrganization: currentObject.properties?.domain
                          ? orgsByDomain[currentObject.properties.domain]
                          : null,
                        title:
                          currentObject.properties?.title ||
                          (currentObject.properties?.domain
                            ? orgsByDomain?.[currentObject.properties.domain]
                                ?.name
                            : null),
                        selectedOwner: currentUser,
                      }}
                      onComplete={() => {
                        setOpen(false)
                      }}
                      onUpdate={(newObject) =>
                        dispatch({ type: 'OBJECT_UPDATED', payload: newObject })
                      }
                      onBeforeClose={beforeCloseRef}
                    />
                  )}
                </>
              ) : currentObject?.mode === 'examine' ? (
                <SidebarPropertyDetail
                  crmObject={currentObject}
                  setOpen={setOpen}
                />
              ) : (
                <>
                  {currentObject?.objectId &&
                    currentObject?.objectType === NativeObjectTypes.Contact && (
                      <SidebarLayoutContact
                        crmObject={currentObject}
                        setOpen={setOpen}
                        workspaceId={workspaceId}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.Organization && (
                      <SidebarLayoutOrganization
                        domain={
                          currentObject.objectId ||
                          currentObject?.properties?.domain
                        }
                        setOpen={setOpen}
                        workspaceId={workspaceId}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.Opportunity && (
                      <SidebarLayoutOpportunity
                        crmObject={currentObject}
                        setOpen={setOpen}
                        workspaceId={workspaceId}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType === NativeObjectTypes.Action && (
                      <SidebarLayoutAction
                        action={{
                          id: currentObject?.objectId,
                          workspaceId: currentObject?.workspaceId,
                          ...currentObject?.properties,
                        }}
                        onClose={() => setOpen(false)}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType === NativeObjectTypes.Event && (
                      <SidebarLayoutEvent
                        workspaceId={currentObject?.workspaceId}
                        crmObject={currentObject}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.GmailThread && (
                      <SidebarLayoutGmailThread crmObject={currentObject} />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.SlackChannel && (
                      <SidebarLayoutSlackChannel crmObject={currentObject} />
                    )}
                  {/* DEPRECATED */}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.MeetingRecording && (
                      <SidebarLayoutMeetingRecording
                        crmObject={currentObject}
                        setOpen={setOpen}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType === NativeObjectTypes.Page && (
                      <SidebarLayoutPage
                        crmObject={currentObject}
                        onClose={() => setOpen(false)}
                      />
                    )}
                  {currentObject?.objectType ===
                    NativeObjectTypes.UserContext && (
                    <SidebarLayoutUserContext
                      crmObject={currentObject}
                      hideSearch={currentObject?.properties?.hideSearch}
                      onUpdate={(newObject) =>
                        dispatch({ type: 'OBJECT_UPDATED', payload: newObject })
                      }
                    />
                  )}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.MeetingRecordingClip && (
                      <SidebarLayoutClip
                        crmObject={currentObject}
                        setOpen={setOpen}
                      />
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType ===
                      NativeObjectTypes.Pipeline && (
                      <PipelineProvider
                        workspaceId={currentObject?.workspaceId}
                        pipelineId={currentObject?.objectId}
                      >
                        <SidebarLayoutPipeline setOpen={setOpen} />
                      </PipelineProvider>
                    )}
                  {currentObject?.objectId &&
                    currentObject?.objectType === NativeObjectTypes.Draft && (
                      <SidebarLayoutDraft crmObject={currentObject} />
                    )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    )
  )
}

export default Sidebar
